import React, { useEffect, useState } from "react";
import { Spinner, Badge, OverlayTrigger, Tooltip, Modal, Button } from "react-bootstrap";
import { FiClock, FiAlertCircle, FiCheckCircle, FiInfo, FiX } from "react-icons/fi";

const MediaFornitoriCard = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFullscreenModal, setShowFullscreenModal] = useState(false);

  useEffect(() => {
    fetch("ordini_urgenti.json")
      .then((res) => res.json())
      .then((json) => {
        const ordini = json?.STATO_ORDINI?.flat() || [];
        const perFornitore = {};

        ordini.forEach((o) => {
          const fornitore = o["Ragione sociale"] || "N/A";
          const targetSpedizione = parseInt(o["Giorni Spedizione"]) || 0;
          const dataOrdine = new Date(o["Data Ordine"]);

          if (isNaN(dataOrdine)) return;

          const dataMagazzinoRaw = o["Data Entrata Magazzino"] || o["Data Entrata Magazzino Acc"];
          const dataMagazzino = dataMagazzinoRaw ? new Date(dataMagazzinoRaw) : null;

          const dataFineRaw = o["Data Completamento ODP/ODPV"] || o["Data Chiusura ODP"] || o["data_fatt"];
          const dataFine = dataFineRaw ? new Date(dataFineRaw) : null;

          const haMagazzino = dataMagazzino && !isNaN(dataMagazzino);
          const haEvasione = dataFine && !isNaN(dataFine);
          if (!haMagazzino && !haEvasione) return;

          if (!perFornitore[fornitore]) {
            perFornitore[fornitore] = {
              totaleGiorniMagazzino: 0,
              countMagazzino: 0,
              inTargetMagazzino: 0,
              totaleGiorniEvasione: 0,
              countEvasione: 0,
              inTargetEvasione: 0,
              target: targetSpedizione,
            };
          }

          if (haMagazzino) {
            const diffDaysMag = Math.ceil((dataMagazzino - dataOrdine) / (1000 * 60 * 60 * 24));
            perFornitore[fornitore].totaleGiorniMagazzino += diffDaysMag;
            perFornitore[fornitore].countMagazzino += 1;

            if (diffDaysMag <= targetSpedizione + 1.5) {
              perFornitore[fornitore].inTargetMagazzino += 1;
            }
          }

          if (haEvasione) {
            const diffDaysEvasione = Math.ceil((dataFine - dataOrdine) / (1000 * 60 * 60 * 24));
            perFornitore[fornitore].totaleGiorniEvasione += diffDaysEvasione;
            perFornitore[fornitore].countEvasione += 1;

            if (diffDaysEvasione <= targetSpedizione + 1.5) {
              perFornitore[fornitore].inTargetEvasione += 1;
            }
          }
        });

        const risultato = Object.entries(perFornitore)
          .map(([fornitore, val]) => {
            const mediaMag = val.countMagazzino > 0
              ? Math.round((val.totaleGiorniMagazzino / val.countMagazzino) * 2) / 2
              : null;

            const mediaEva = val.countEvasione > 0 
              ? Math.round((val.totaleGiorniEvasione / val.countEvasione) * 2) / 2 
              : null;

            const pctMagazzino = val.countMagazzino > 0
              ? Math.round((val.inTargetMagazzino / val.countMagazzino) * 100)
              : null;

            const pctEvasione = val.countEvasione > 0
              ? Math.round((val.inTargetEvasione / val.countEvasione) * 100)
              : null;

            const getPctStyle = (pct) => {
              if (pct === null) return { bg: "#f1f3f5", text: "#adb5bd" };
              if (pct > 75) return { bg: "#e6fcf5", text: "#099268" };
              if (pct >= 50) return { bg: "#fff9db", text: "#f08000" };
              return { bg: "#fff5f5", text: "#e03131" };
            };

            let statusMag = "danger";
            if (mediaMag === null) statusMag = "none";
            else if (mediaMag <= val.target) statusMag = "success";
            else if (mediaMag <= val.target + 1.5) statusMag = "warning";

            let statusEva = "danger";
            if (mediaEva === null) statusEva = "none";
            else if (mediaEva <= val.target) statusEva = "success";
            else if (mediaEva <= val.target + 1.5) statusEva = "warning";

            return {
              fornitore,
              target: val.target,
              countMagazzino: val.countMagazzino,
              countEvasione: val.countEvasione,
              mediaMagazzino: mediaMag !== null ? mediaMag.toFixed(1) + " gg" : "--",
              mediaEvasione: mediaEva !== null ? mediaEva.toFixed(1) + " gg" : "--",
              pctMagazzino,
              styleMag: getPctStyle(pctMagazzino),
              pctEvasione,
              styleEva: getPctStyle(pctEvasione),
              statusMag,
              statusEva
            };
          })
          .sort((a, b) => a.fornitore.localeCompare(b.fornitore));

        setData(risultato);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Errore nel caricamento dei dati JSON:", err);
        setLoading(false);
      });
  }, []);

  const renderBadgeStatus = (status) => {
    if (status === "none") return <span className="text-muted text-center d-block">--</span>;
    
    const config = {
      success: { bg: "success-subtle", text: "text-success", border: "border-success-alpha", icon: <FiCheckCircle size={10} />, label: "In Target" },
      warning: { bg: "warning-subtle", text: "text-warning", border: "border-warning-alpha", icon: <FiClock size={10} />, label: "Alert" },
      danger: { bg: "danger-subtle", text: "text-danger", border: "border-danger-alpha", icon: <FiAlertCircle size={10} />, label: "Ritardo" }
    };

    const current = config[status];
    return (
      <Badge bg={current.bg} className={`status-badge-mini ${current.text} border ${current.border} text-uppercase`}>
        {current.icon} {current.label}
      </Badge>
    );
  };

  const renderTooltip = (props) => (
    <Tooltip id="info-performance-tooltip" {...props}>
      <div style={{ textAlign: 'left', fontSize: '0.7rem', padding: '5px' }}>
        <strong>CONFRONTO INDIPENDENTE TOTALE:</strong><br/>
        • <strong>FORNITORE - MAGAZZINO:</strong> Mostra i dati estratti basandosi sulle date di entrata a magazzino.<br/>
        • <strong>FORNITORE - CLIENTE:</strong> Mostra i dati estratti analizzando le date di chiusura ODP/ODPV/Fatturazione.<br/>
        <hr style={{ margin: '5px 0', borderColor: 'rgba(255,255,255,0.2)' }} />
        <em>Le colonne calcolano i record in modo autonomo: se un ordine ha solo la chiusura e non lo scarico di magazzino, verrà comunque conteggiato correttamente nell'Evasione Totale.</em>
      </div>
    </Tooltip>
  );

  // Struttura della tabella estratta per renderizzarla sia nella card che nel modale
  const renderTableContent = () => (
    <table className="table table-hover mb-0" style={{ fontSize: "0.70rem" }}>
      <thead className="bg-white sticky-top" style={{ zIndex: 1, boxShadow: "0 2px 2px -1px rgba(0,0,0,0.05)" }}>
        <tr>
          <th className="border-0 text-muted ps-3" rowSpan={2} style={{ verticalAlign: "middle" }}>FORNITORE</th>
          <th className="border-0 text-muted text-center border-end" rowSpan={2} style={{ verticalAlign: "middle", width: "70px" }}>TARGET</th>
          <th className="border-0 text-primary text-center bg-light" colSpan={2} style={{ fontSize: "0.65rem", fontWeight: "800", borderBottom: "2px solid #0d6efd" }}>FORNITORE - MAGAZZINO</th>
          <th className="border-0 text-purple text-center" colSpan={2} style={{ fontSize: "0.65rem", fontWeight: "800", borderBottom: "2px solid #6f42c1", backgroundColor: "#fbfaff" }}>FORNITORE - CLIENTE</th>
        </tr>
        <tr>
          <th className="text-center bg-light text-muted fw-bold">MEDIA / AFFID.</th>
          <th className="text-center bg-light text-muted fw-bold border-end">STATUS</th>
          <th className="text-center text-muted fw-bold" style={{ backgroundColor: "#fbfaff" }}>MEDIA / AFFID.</th>
          <th className="text-end text-muted fw-bold pe-3" style={{ backgroundColor: "#fbfaff" }}>STATUS</th>
        </tr>
      </thead>
      <tbody>
        {data.map((f, i) => (
          <tr key={i} style={{ verticalAlign: "middle" }}>
            <td className="ps-3">
              <div className="fw-bold" style={{ lineHeight: "1.2", color: "#333", maxWidth: "180px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {f.fornitore}
              </div>
              <div style={{ fontSize: "0.58rem", color: "#999" }}>
                Campione: Mag ({f.countMagazzino}) | Evas ({f.countEvasione})
              </div>
            </td>

            <td className="text-center text-muted border-end fw-bold">{f.target} gg</td>
            
            <td className="text-center bg-light">
              <span className="fw-bold text-primary d-block" style={{ fontSize: "0.75rem" }}>{f.mediaMagazzino}</span>
              {f.pctMagazzino !== null ? (
                <div className="badge-small-pct mt-1" style={{ backgroundColor: f.styleMag.bg, color: f.styleMag.text }}>
                  {f.pctMagazzino}% 
                </div>
              ) : (
                <span className="text-muted" style={{ fontSize: "0.6rem" }}>Nessun dato</span>
              )}
            </td>
            <td className="text-center bg-light border-end">
              {renderBadgeStatus(f.statusMag)}
            </td>
            
            <td className="text-center" style={{ backgroundColor: "#fbfaff" }}>
              <span className="fw-bold d-block" style={{ fontSize: "0.75rem", color: "#6f42c1" }}>{f.mediaEvasione}</span>
              {f.pctEvasione !== null ? (
                <div className="badge-small-pct mt-1" style={{ backgroundColor: f.styleEva.bg, color: f.styleEva.text }}>
                  {f.pctEvasione}% 
                </div>
              ) : (
                <span className="text-muted" style={{ fontSize: "0.6rem" }}>Nessun dato</span>
              )}
            </td>
            <td className="text-end pe-3" style={{ backgroundColor: "#fbfaff" }}>
              {renderBadgeStatus(f.statusEva)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  return (
    <div className="ombra card tb" style={{ borderRadius: "12px", border: "1px solid #dee2e6", height: "450px", display: "flex", flexDirection: "column", overflow: "hidden", backgroundColor: "#fff" }}>
      <div className="p-3 border-bottom d-flex justify-content-between align-items-center bg-white" style={{ zIndex: 110 }}>
        <div className="d-flex align-items-center" onClick={() => setShowFullscreenModal(true)} style={{ cursor: "pointer" }}>
          <span style={{ fontWeight: "800", fontSize: "0.75rem", color: "#1a1a1a", letterSpacing: "1px", marginRight: "8px" }}>
            PERFORMANCE FORNITORI
          </span>
          <OverlayTrigger placement="bottom" overlay={renderTooltip}>
            <span className="ms-2" onClick={(e) => e.stopPropagation()} style={{ cursor: "pointer" }}><FiInfo size={14} color="#adb5bd" /></span>
          </OverlayTrigger>
        </div>
      </div>

      <div className="scroll-container" style={{ flexGrow: 1, overflowY: "auto" }}>
        <style>{`
          .scroll-container::-webkit-scrollbar { width: 4px; }
          .scroll-container::-webkit-scrollbar-thumb { background: #ccc; border-radius: 10px; }
          .badge-small-pct { padding: 4px 6px; font-size: 0.65rem; font-weight: 800; border-radius: 4px; min-width: 42px; display: inline-block; text-align: center; }
          .status-badge-mini { padding: 4px 6px; font-size: 0.58rem; display: inline-flex; align-items: center; gap: 3px; letter-spacing: 0.5px; white-space: nowrap; }
          .table td { padding: 12px 8px !important; }
        `}</style>

        {loading ? (
          <div className="d-flex justify-content-center align-items-center h-100">
            <Spinner animation="border" variant="danger" size="sm" />
          </div>
        ) : (
          renderTableContent()
        )}
      </div>

      {/* MODALE TUTTO SCHERMO */}
      <Modal show={showFullscreenModal} onHide={() => setShowFullscreenModal(false)} fullscreen={true} animation={true}>
        <Modal.Body className="p-0 d-flex flex-column" style={{ background: "#f8f9fa" }}>
          <div className="p-3 bg-white border-bottom d-flex justify-content-between align-items-center shadow-sm" style={{ zIndex: 110 }}>
            <div className="d-flex align-items-center">
              <span style={{ fontWeight: "800", fontSize: "0.9rem", color: "#1a1a1a", letterSpacing: "1px", marginRight: "8px" }}>
                PERFORMANCE FORNITORI
              </span>
              <OverlayTrigger placement="bottom" overlay={renderTooltip}>
                <span style={{ cursor: "pointer" }}><FiInfo size={16} color="#adb5bd" /></span>
              </OverlayTrigger>
            </div>
            <Button variant="outline-dark" className="d-flex align-items-center gap-1 border-0" onClick={() => setShowFullscreenModal(false)} style={{ borderRadius: "10px", height: "38px" }}>
              <FiX size={16} /> 
            </Button>
          </div>
          <div className="flex-grow-1 p-4" style={{ overflowY: "auto" }}>
            <div className="bg-white p-3 shadow-sm rounded-4 border scroll-container">
              {loading ? (
                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "300px" }}>
                  <Spinner animation="border" variant="danger" size="sm" />
                </div>
              ) : (
                renderTableContent()
              )}
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default MediaFornitoriCard;